import React from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiLayers,
  FiCloud,
  FiGitMerge,
  FiRefreshCw,
  FiPlus,
  FiCheck,
} from "react-icons/fi"
import Link from "../../components/Link"
import PageMetadata from "../../components/PageMetadata"
import { translateMessageId } from "../../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.78) 0%,
      rgba(4, 14, 24, 0.55) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible,
  summary:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`
const HeroContent = styled(Container)`
  min-height: 610px;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    max-width: 660px;
    color: #fff;
    font-size: clamp(2.75rem, 5.1vw, 4.5rem);
    font-weight: 500;
    line-height: 1.07;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 500px;
    color: #e0e6ec;
    font-size: 1.1rem;
    line-height: 1.75;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    flex-shrink: 0;
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  background: #fff;
  color: #102030 !important;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`
const SectionNav = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    padding: 1.4rem 1.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    text-decoration: none;
  }
  a:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1rem 0;
      border-left: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`
const Capabilities = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`
const Capability = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  padding: 2rem;
  display: grid;
  grid-template-columns: 2rem 1fr;
  gap: 1.5rem;
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 0.1rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 400px) {
    padding: 1.5rem;
    gap: 1rem;
    grid-template-columns: 1.5rem 1fr;
  }
`
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`
const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`
const Contact = styled(Section)`
  color: #fff;
  background: #0b1b2a;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3rem;
  }
  ${Kicker} {
    color: #b4cae7;
  }
  h2 {
    color: #fff;
    max-width: 640px;
  }
  p:last-child {
    color: #d9e2eb;
    max-width: 670px;
    line-height: 1.75;
    font-size: 1.05rem;
    margin: 0;
  }
  a:focus-visible,
  summary:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 950px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    a {
      justify-self: start;
    }
  }
`

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 4rem;
  align-items: start;
  ${SectionIntro} {
    margin-bottom: 0;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const Design = styled(Section)`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Principles = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 0;
  li {
    display: grid;
    grid-template-columns: 1.1rem 1fr;
    gap: 0.8rem;
    margin: 0 0 1rem;
    font-size: 0.95rem;
    line-height: 1.6;
  }
  li::before {
    display: none;
  }
  svg {
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 0.3rem;
  }
`
const Blueprint = styled.figure`
  margin: 0;
  padding: 2rem;
  background: #0b1b2a;
  color: #fff;
  > p {
    color: #abc0d9;
    margin: 0 0 2rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    line-height: 1.5;
  }
  > svg {
    display: block;
    color: #84adf6;
    margin: 0.7rem auto;
  }
  figcaption {
    font-size: 0.8rem;
    line-height: 1.6;
    color: #abc0d9;
    margin-top: 1.5rem;
  }
  @media (max-width: 450px) {
    padding: 1.5rem;
  }
`
const DiagramLayer = styled.div`
  border: 1px solid #425369;
  background: #152637;
  padding: 1rem;
  text-align: center;
  strong {
    display: block;
    color: #fff;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.4;
  }
  span {
    display: block;
    color: #bcccdc;
    margin-top: 0.45rem;
    font-size: 0.85rem;
    line-height: 1.6;
  }
`
const DiagramPair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  @media (max-width: 450px) {
    grid-template-columns: 1fr;
  }
`
const DiagramFoundation = styled.div`
  border-top: 2px solid #84adf6;
  margin-top: 1.5rem;
  padding-top: 1rem;
  text-align: center;
  font-size: 0.8rem;
  line-height: 1.7;
  color: #d6e2ef;
`
const Deliverables = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const DeliverableList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    margin: 0;
    padding: 1.5rem 0;
  }
  li:first-child {
    padding-top: 0;
    border-top: 0;
  }
  li:last-child {
    padding-bottom: 0;
  }
  li::before {
    display: none;
  }
  > li > span {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    padding-top: 0.25rem;
  }
  h3 {
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 0.95rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`
const FAQSection = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
`
const Question = styled.details`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.4rem 0;
    cursor: pointer;
    list-style: none;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.5;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  &[open] summary svg {
    transform: rotate(45deg);
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.75;
    margin: 0 0 1.5rem;
  }
`

const capabilities = [
  { id: "software", icon: FiLayers },
  { id: "cloud", icon: FiCloud },
  { id: "integration", icon: FiGitMerge },
  { id: "modernization", icon: FiRefreshCw },
]
const deliverables = ["assessment", "diagrams", "decisions", "roadmap"]
const steps = ["understand", "compare", "validate", "transfer"]
const questions = ["microservices", "existing", "implementation"]
const related = [
  { id: "development", to: "/desarrollo/" },
  { id: "security", to: "/devsecops/" },
  { id: "ai", to: "/ai-service/" },
]

const ArchitecturePage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`architecture-${id}`, intl)
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt=""
          loading="eager"
          fadeIn={false}
          imgStyle={{ objectPosition: "78% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("title")}</h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")} <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#servicios">
              {t("explore")} <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </Hero>
      <SectionNav aria-label={t("navigation")}>
        <Container>
          <Link to="#servicios">
            {t("nav-services")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#entregables">
            {t("nav-deliverables")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#metodo">
            {t("nav-method")} <FiArrowDown aria-hidden="true" />
          </Link>
        </Container>
      </SectionNav>
      <Section id="servicios" aria-labelledby="architecture-services-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("services-eyebrow")}</Kicker>
            <h2 id="architecture-services-title">{t("services-title")}</h2>
            <p>{t("services-intro")}</p>
          </SectionIntro>
          <Capabilities>
            {capabilities.map(({ id, icon: Icon }) => (
              <Capability key={id}>
                <Icon size={26} aria-hidden="true" />
                <div>
                  <h3>{t(`${id}-title`)}</h3>
                  <p>{t(`${id}-description`)}</p>
                </div>
              </Capability>
            ))}
          </Capabilities>
        </Container>
      </Section>
      <Design aria-labelledby="architecture-design-title">
        <Container>
          <Split>
            <SectionIntro>
              <Kicker>{t("design-eyebrow")}</Kicker>
              <h2 id="architecture-design-title">{t("design-title")}</h2>
              <p>{t("design-intro")}</p>
              <Principles>
                {["security", "reliability", "efficiency"].map((id) => (
                  <li key={id}>
                    <FiCheck aria-hidden="true" />
                    <span>{t(`principle-${id}`)}</span>
                  </li>
                ))}
              </Principles>
            </SectionIntro>
            <Blueprint>
              <p>{t("diagram-eyebrow")}</p>
              <DiagramLayer>
                <strong>{t("diagram-channels")}</strong>
                <span>{t("diagram-channels-detail")}</span>
              </DiagramLayer>
              <FiArrowDown aria-hidden="true" />
              <DiagramLayer>
                <strong>{t("diagram-services")}</strong>
                <span>{t("diagram-services-detail")}</span>
              </DiagramLayer>
              <FiArrowDown aria-hidden="true" />
              <DiagramPair>
                <DiagramLayer>
                  <strong>{t("diagram-data")}</strong>
                  <span>{t("diagram-data-detail")}</span>
                </DiagramLayer>
                <DiagramLayer>
                  <strong>{t("diagram-integration")}</strong>
                  <span>{t("diagram-integration-detail")}</span>
                </DiagramLayer>
              </DiagramPair>
              <DiagramFoundation>{t("diagram-foundation")}</DiagramFoundation>
              <figcaption>{t("diagram-caption")}</figcaption>
            </Blueprint>
          </Split>
        </Container>
      </Design>
      <Deliverables
        id="entregables"
        aria-labelledby="architecture-deliverables-title"
      >
        <Container>
          <Split>
            <SectionIntro>
              <Kicker>{t("deliverables-eyebrow")}</Kicker>
              <h2 id="architecture-deliverables-title">
                {t("deliverables-title")}
              </h2>
              <p>{t("deliverables-intro")}</p>
            </SectionIntro>
            <DeliverableList>
              {deliverables.map((id, index) => (
                <li key={id}>
                  <span aria-hidden="true">0{index + 1}</span>
                  <div>
                    <h3>{t(`${id}-title`)}</h3>
                    <p>{t(`${id}-description`)}</p>
                  </div>
                </li>
              ))}
            </DeliverableList>
          </Split>
        </Container>
      </Deliverables>
      <Section id="metodo" aria-labelledby="architecture-method-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("method-eyebrow")}</Kicker>
            <h2 id="architecture-method-title">{t("method-title")}</h2>
            <p>{t("method-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`${id}-title`)}</h3>
                <p>{t(`${id}-description`)}</p>
              </li>
            ))}
          </Steps>
          <Related aria-labelledby="architecture-related-title">
            <h2 id="architecture-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)} <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </Section>
      <FAQSection aria-labelledby="architecture-faq-title">
        <Container>
          <Split>
            <SectionIntro>
              <Kicker>{t("faq-eyebrow")}</Kicker>
              <h2 id="architecture-faq-title">{t("faq-title")}</h2>
              <p>{t("faq-intro")}</p>
            </SectionIntro>
            <div>
              {questions.map((id) => (
                <Question key={id}>
                  <summary>
                    {t(`faq-${id}-question`)}
                    <FiPlus size={20} aria-hidden="true" />
                  </summary>
                  <p>{t(`faq-${id}-answer`)}</p>
                </Question>
              ))}
            </div>
          </Split>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="architecture-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="architecture-contact-title">{t("cta-title")}</h2>
            <p>{t("cta-description")}</p>
          </div>
          <PrimaryLink to="/#contacto">
            {t("contact")} <FiArrowRight aria-hidden="true" />
          </PrimaryLink>
        </Container>
      </Contact>
    </Page>
  )
}
export default ArchitecturePage
export const query = graphql`
  query ArchitectureDetailPageQuery {
    hero: file(relativePath: { eq: "arquitectura/solution-design-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
